import React from 'react';
import styled from 'styled-components';
import { convertPrice } from 'utils/convertPrice';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { ReportData } from 'utils/types/AnalyticPanelReport';

import { PDF_EXPORT_CLASS } from './pdfExportClass';

const HeaderWrapper = styled.div`
    margin-bottom: 20px;

    .${PDF_EXPORT_CLASS} & {
        margin-bottom: 28px;
    }
`;

const TopHeader = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 24px 32px;

    .${PDF_EXPORT_CLASS} & {
        padding: 36px 40px;
        border: 1px solid var(--pdf-card-border);
    }
`;

const HeaderTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
`;

const HeaderTitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-primary-70);

    .${PDF_EXPORT_CLASS} & {
        gap: 12px;
    }
`;

const HeaderTitle = styled.h1`
    margin: 8px 0 4px;

    .${PDF_EXPORT_CLASS} & {
        margin: 0;
        font-size: 34px;
        font-weight: 700;
    }
`;

const HeaderAddress = styled.div`
    color: var(--color-primary-70);

    .${PDF_EXPORT_CLASS} & {
        font-size: 22px;
        font-weight: 600;
        color: var(--color-primary);
    }
`;

const PropertyMeta = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    .${PDF_EXPORT_CLASS} & {
        margin-top: 8px;
    }
`;

const PropertyMetaItem = styled.span`
    font-size: 12px;
    color: var(--color-primary-70);

    strong {
        color: var(--color-primary);
        font-weight: 600;
    }

    .${PDF_EXPORT_CLASS} & {
        padding: 8px 16px;
        border-radius: 20px;
        background: var(--color-alt-second);
        font-size: 16px;

        strong {
            font-size: 18px;
        }
    }
`;

const HeaderDateSection = styled.div`
    text-align: right;

    .${PDF_EXPORT_CLASS} & {
        padding: 20px 24px;
        border-radius: var(--box-border-radius);
        background: var(--color-alt-second);
    }
`;

const DateLabel = styled.div`
    color: var(--color-primary-70);
    margin-bottom: 4px;

    .${PDF_EXPORT_CLASS} & {
        font-size: 15px;
        margin-bottom: 8px;
    }
`;

const DateValue = styled.div`
    font-size: 14px;
    font-weight: 500;

    .${PDF_EXPORT_CLASS} & {
        font-size: 22px;
        font-weight: 700;
        color: var(--color-primary);
    }
`;

const DateSubtext = styled.div`
    font-size: 13px;
    color: var(--color-primary-70);

    .${PDF_EXPORT_CLASS} & {
        font-size: 16px;
        margin-top: 4px;
    }
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    margin-top: 20px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }

    .${PDF_EXPORT_CLASS} & {
        grid-template-columns: repeat(6, 1fr);
        gap: 20px;
        margin-top: 24px;
    }
`;

// W PDF: trzy kwoty w pierwszym rzędzie, wynik inwestycji (zysk, marża) wyróżniony w drugim
// Reguły pod klasą PDF trafiają we wspólną klasę komponentu, a nie w wariant z propsami,
// dlatego różnice między kartami przekazujemy zmiennymi CSS
const MetricCard = styled.div<{ $highlighted?: boolean }>`
    padding: 20px 24px;
    border-radius: var(--box-border-radius);
    border: 1px solid var(--color-alt-second);
    background: var(--color-white);
    --pdf-metric-span: ${({ $highlighted }) => $highlighted ? 3 : 2};
    --pdf-metric-border: ${({ $highlighted }) => $highlighted ? '2px solid var(--color-alt)' : '1px solid var(--pdf-card-border)'};
    --pdf-metric-value-color: ${({ $highlighted }) => $highlighted ? 'var(--color-alt)' : 'var(--color-primary)'};

    .${PDF_EXPORT_CLASS} & {
        grid-column: span var(--pdf-metric-span);
        padding: 28px 32px;
        border: var(--pdf-metric-border);
        --metric-value-color: var(--pdf-metric-value-color);
    }
`;

const MetricLabel = styled.div`
    font-size: 11px;
    color: var(--color-primary-70);
    margin-bottom: 4px;

    .${PDF_EXPORT_CLASS} & {
        font-size: 15px;
        margin-bottom: 8px;
    }
`;

const MetricValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: var(--metric-value-color, var(--color-primary));
    margin-bottom: 4px;

    .${PDF_EXPORT_CLASS} & {
        font-size: 32px;
    }
`;

const MetricSubtext = styled.div`
    font-size: 13px;
    color: var(--color-primary-70);

    .${PDF_EXPORT_CLASS} & {
        font-size: 15px;
    }
`;

interface ReportHeaderProps {
    data: ReportData;
}

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);

    return date.toLocaleDateString('pl-PL');
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({ data }) => {
    const { address, floor, area, rooms, purchaseDate, saleDate, purchasePrice, salePrice: salePriceRaw, investmentTime, calculation, costs } = data;

    const profit = stringToNumberOrZero(calculation?.estimatedProfitNetto);
    const salePrice = stringToNumberOrZero(salePriceRaw);
    const margin = stringToNumberOrZero(calculation?.margin);
    const totalCosts = stringToNumberOrZero(costs?.sumOfCosts);

    return (
        <HeaderWrapper>
            <TopHeader>
                <HeaderTopRow>
                    <HeaderTitleContainer>
                        <HeaderTitle>Raport Inwestycyjny</HeaderTitle>
                        <HeaderAddress>{address || 'Brak adresu'}</HeaderAddress>
                        <PropertyMeta>
                            {area &&
                                <PropertyMetaItem>
                                    Metraż: <strong>{area} m²</strong>
                                </PropertyMetaItem>
                            }
                            {rooms &&
                                <PropertyMetaItem>
                                    Pokoje: <strong>{rooms}</strong>
                                </PropertyMetaItem>
                            }
                            {floor &&
                                <PropertyMetaItem>
                                    Piętro: <strong>{floor}</strong>
                                </PropertyMetaItem>
                            }
                        </PropertyMeta>
                    </HeaderTitleContainer>
                    <HeaderDateSection>
                        <DateLabel>Okres inwestycji</DateLabel>
                        <DateValue>{formatDate(purchaseDate)} - {formatDate(saleDate)}</DateValue>
                        <DateSubtext>{stringToNumberOrZero(investmentTime)} miesięcy</DateSubtext>
                    </HeaderDateSection>
                </HeaderTopRow>
            </TopHeader>

            <MetricsRow>
                <MetricCard>
                    <MetricLabel>Cena mieszkania</MetricLabel>
                    <MetricValue>{convertPrice(stringToNumberOrZero(purchasePrice))} zł</MetricValue>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Suma kosztów</MetricLabel>
                    <MetricValue>{convertPrice(totalCosts)} zł</MetricValue>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Cena sprzedaży</MetricLabel>
                    <MetricValue>{convertPrice(salePrice)} zł</MetricValue>
                </MetricCard>

                <MetricCard $highlighted>
                    <MetricLabel>Całkowity zysk</MetricLabel>
                    <MetricValue>{convertPrice(profit)} zł</MetricValue>
                </MetricCard>

                <MetricCard $highlighted>
                    <MetricLabel>Marża brutto</MetricLabel>
                    <MetricValue>{margin.toFixed(2)}%</MetricValue>
                    <MetricSubtext>Zysk / Cena sprzedaży</MetricSubtext>
                </MetricCard>
            </MetricsRow>

        </HeaderWrapper>
    );
};
